import React from "react";
import { FilterState } from "@/components/FilterSidebar/FilterState";
import { StudentOrdering } from "@/types/StudentOrdering";
import { FilterContext } from "@/types/FilterContext";
import { useRoute } from "@/routes/getRoute";
import { useBuildUrlState } from "@/hooks/useUrlSerializer";
import { useProtectedNavigation } from "./useProtectedNavigation";
import {
  ProtectedNavLinkProps,
  ProtectedNavLink,
} from "@/components/ProtectedNavigationLink";
import { AnalyticsEvent, useAnalytics } from "@/hooks/useAnalytics";

export const useStudentsListLink = () => {
  const getRoute = useRoute();
  const buildFilterUrlState = useBuildUrlState("FILTER_STATE");
  const buildContextUrlState = useBuildUrlState("FILTER_CONTEXT");
  const buildOrderingUrlState = useBuildUrlState("STUDENT_ORDERING");

  return (
    queryData: {
      fs?: FilterState;
      ordering?: StudentOrdering;
      context?: FilterContext;
    },
    pathOnly = false,
  ) => {
    const filterParams = buildFilterUrlState(queryData.fs);
    const contextParams = buildContextUrlState(queryData.context);
    const orderingParams = buildOrderingUrlState(queryData.ordering);

    const q = {
      ...filterParams,
      ...contextParams,
      ...orderingParams,
    };
    return getRoute("students", {}, q, !pathOnly);
  };
};

export const useProtectedNavToStudent = () => {
  const buildFilterUrlState = useBuildUrlState("FILTER_STATE");
  const buildContextUrlState = useBuildUrlState("FILTER_CONTEXT");
  const buildOrderingUrlState = useBuildUrlState("STUDENT_ORDERING");
  const { trackEvent } = useAnalytics();

  const { navToRoute } = useProtectedNavigation();

  return (
    {
      fs,
      ordering,
      context,
    }: {
      fs?: FilterState;
      ordering?: StudentOrdering;
      context?: FilterContext;
    },
    source?: string,
  ) => {
    if (source) {
      trackEvent(AnalyticsEvent.OpenedStudentList, { Source: source });
    }

    const filterParams = buildFilterUrlState(fs);
    const contextParams = buildContextUrlState(context);
    const orderingParams = buildOrderingUrlState(ordering);

    const q = {
      ...filterParams,
      ...contextParams,
      ...orderingParams,
    };

    navToRoute("students", {}, q);
  };
};

interface ProtectedStudentNavProps
  extends Omit<ProtectedNavLinkProps, "route"> {
  fs?: FilterState;
  ordering?: StudentOrdering;
  context?: FilterContext;
  source?: string;
}

export const ProtectedStudentListLink = ({
  fs,
  context,
  ordering,
  source,
  ref,
  ...navLinkProps
}: ProtectedStudentNavProps & {
  ref?: React.RefObject<HTMLAnchorElement | null>;
}) => {
  const buildFilterUrlState = useBuildUrlState("FILTER_STATE");
  const buildContextUrlState = useBuildUrlState("FILTER_CONTEXT");
  const buildOrderingUrlState = useBuildUrlState("STUDENT_ORDERING");

  const filterParams = buildFilterUrlState(fs);
  const contextParams = buildContextUrlState(context);
  const orderingParams = buildOrderingUrlState(ordering);

  const q = {
    ...filterParams,
    ...contextParams,
    ...orderingParams,
  };

  return (
    <ProtectedNavLink
      ref={ref}
      route="students"
      {...navLinkProps}
      query={q}
      analyticsEvent={source ? AnalyticsEvent.OpenedStudentList : undefined}
      analyticsProperties={source ? { Source: source } : undefined}
    />
  );
};
